import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ShoppingBasket } from 'lucide-react';

export function RequestBasketBar({ count, totalLabel, onOpen }) {
  const [showPayNow, setShowPayNow] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = setInterval(() => {
      setShowPayNow((prev) => !prev);
    }, 2200);
    return () => clearInterval(id);
  }, [reduceMotion]);

  if (!count || typeof document === 'undefined') return null;

  const countLabel = count === 1 ? '1 song' : `${count} songs`;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 z-[9990] flex justify-center px-3"
      style={{
        bottom: 'max(0.75rem, env(safe-area-inset-bottom))',
      }}
    >
      <style>{`
        @keyframes ysrBasketGlow {
          0%, 100% {
            filter:
              drop-shadow(0 0 4px rgba(237, 233, 254, 0.95))
              drop-shadow(0 0 12px rgba(167, 139, 250, 0.85))
              drop-shadow(0 0 22px rgba(124, 58, 237, 0.55));
          }
          50% {
            filter:
              drop-shadow(0 0 8px rgba(255, 255, 255, 1))
              drop-shadow(0 0 18px rgba(196, 181, 255, 1))
              drop-shadow(0 0 32px rgba(139, 92, 246, 0.8));
          }
        }
        @keyframes ysrPayNowGlow {
          0%, 100% { text-shadow: 0 0 8px rgba(167, 243, 208, 0.55), 0 0 18px rgba(52, 211, 153, 0.4); }
          50% { text-shadow: 0 0 12px rgba(236, 253, 245, 0.95), 0 0 24px rgba(52, 211, 153, 0.75); }
        }
      `}</style>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${countLabel} in basket, total ${totalLabel}. Pay now.`}
        className="pointer-events-auto grid w-full max-w-lg grid-cols-3 items-center rounded-2xl border border-violet-300/70 bg-[#1a1230]/95 px-4 py-[1.15rem] min-h-[4.25rem] shadow-[0_16px_40px_rgba(0,0,0,0.55),0_0_28px_rgba(139,92,246,0.45)] backdrop-blur-xl transition-transform active:scale-[0.98]"
      >
        <span className="text-left text-base font-semibold tracking-wide text-white">
          {countLabel}
        </span>
        <span className="relative flex h-8 items-center justify-center overflow-hidden" aria-hidden>
          {reduceMotion ? (
            <span className="flex items-center gap-1.5 text-emerald-200">
              <ShoppingBasket className="h-7 w-7 text-violet-100" strokeWidth={2} />
              <span className="text-sm font-bold uppercase tracking-wide">Pay Now</span>
            </span>
          ) : (
            <AnimatePresence mode="wait" initial={false}>
              {showPayNow ? (
                <motion.span
                  key="pay-now"
                  initial={{ opacity: 0, y: 8, scale: 0.92 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.92 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="text-base font-bold uppercase tracking-[0.14em] text-emerald-200"
                  style={{ animation: 'ysrPayNowGlow 1.6s ease-in-out infinite' }}
                >
                  Pay Now
                </motion.span>
              ) : (
                <motion.span
                  key="basket"
                  initial={{ opacity: 0, y: 8, scale: 0.92 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.92 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center justify-center text-violet-100"
                  style={{ animation: 'ysrBasketGlow 1.6s ease-in-out infinite' }}
                >
                  <ShoppingBasket className="h-7 w-7" strokeWidth={2.25} />
                </motion.span>
              )}
            </AnimatePresence>
          )}
        </span>
        <span className="text-right text-base font-bold tabular-nums text-emerald-300">
          {totalLabel}
        </span>
      </button>
    </div>,
    document.body
  );
}
